import { ReactNode } from 'react';
import { Flex, Text, Button, Icon } from '@servicetitan/anvil2';
import WarningIcon from '@servicetitan/anvil2/assets/icons/material/round/warning.svg';

export type ErrorFallbackSeverity = 'error' | 'warning';
export type ErrorFallbackLayout = 'centered' | 'inline';
export type ErrorFallbackSize = 'full' | 'compact';

export interface ErrorFallbackUIProps {
  /**
   * Title of the error (e.g., "Feature Name Error")
   */
  title: string;

  /**
   * Optional descriptive message
   */
  message?: string;

  /**
   * Severity affects color scheme:
   * - 'error': Red colors (critical failures)
   * - 'warning': Yellow colors (recoverable issues)
   */
  severity: ErrorFallbackSeverity;

  /**
   * Layout style:
   * - 'centered': Centered content, used for full-page features
   * - 'inline': Inline banner, used for smaller modules
   */
  layout: ErrorFallbackLayout;

  /**
   * Size variant:
   * - 'full': More padding and spacing
   * - 'compact': Reduced padding for smaller areas
   */
  size: ErrorFallbackSize;

  /**
   * Whether this is a permanent error (max retries exceeded)
   * Permanent errors use red colors and may hide retry button
   */
  permanent?: boolean;

  /**
   * Retry handler
   */
  onRetry?: () => void;

  /**
   * Go back handler (typically for centered layout)
   */
  onGoBack?: () => void;

  /**
   * Report bug handler
   */
  onReportBug?: () => void;

  /**
   * Test ID for the root element
   */
  testId?: string;
}

interface ColorScheme {
  bgColor: string;
  borderColor: string;
  textColor: string;
  iconColor: string;
}

type GapSize = 0 | 1 | 2 | 3;

interface SizeConfig {
  padding: string;
  gap: GapSize;
  iconSize: 'small' | 'medium';
  textSize: 'small' | undefined;
  buttonSize: 'small' | undefined;
}

const getColorScheme = (
  severity: ErrorFallbackSeverity,
  permanent: boolean,
): ColorScheme => {
  const isError = severity === 'error' || permanent;
  return {
    bgColor: isError ? 'bg-red-100' : 'bg-yellow-100',
    borderColor: isError ? 'border-red-300' : 'border-yellow-500',
    textColor: isError ? 'c-red-700' : 'c-yellow-800',
    iconColor: isError ? 'c-red-700' : 'c-yellow-800',
  };
};

const getSizeConfig = (
  size: ErrorFallbackSize,
  layout: ErrorFallbackLayout,
): SizeConfig => {
  const isFull = size === 'full';
  if (layout === 'centered') {
    return {
      padding: isFull ? 'p-5' : 'p-3',
      gap: (isFull ? 3 : 2) as GapSize,
      iconSize: 'medium',
      textSize: isFull ? undefined : 'small',
      buttonSize: isFull ? undefined : 'small',
    };
  }
  return {
    padding: isFull ? 'p-2' : 'p-1',
    gap: (isFull ? 2 : 1) as GapSize,
    iconSize: isFull ? 'medium' : 'small',
    textSize: isFull ? undefined : 'small',
    buttonSize: 'small',
  };
};

const renderCenteredLayout = (
  props: ErrorFallbackUIProps,
  colors: ColorScheme,
  sizeConfig: SizeConfig,
): ReactNode => {
  const {
    title,
    message,
    permanent,
    onRetry,
    onGoBack,
    onReportBug,
    testId,
    size,
  } = props;
  const isFull = size === 'full';
  const minHeight = isFull ? 'min-h-400' : '';
  const headlineEl = isFull ? 'h3' : 'h4';

  return (
    <Flex
      data-testid={testId}
      direction='column'
      alignItems='center'
      justifyContent='center'
      className={`${minHeight} ${sizeConfig.padding} ta-center`}
    >
      <Flex
        direction='column'
        gap={sizeConfig.gap}
        alignItems='center'
        className={isFull ? 'max-w-500' : undefined}
      >
        <Text el={headlineEl} variant='headline' className={colors.textColor}>
          {title}
        </Text>

        {message && (
          <Text variant='body' size={isFull ? undefined : 'small'}>
            {message}
          </Text>
        )}

        <Flex direction='row' gap={2}>
          {onRetry && !permanent && (
            <Button
              data-testid='retry-button'
              onClick={onRetry}
              appearance='primary'
              size={sizeConfig.buttonSize}
            >
              Try Again
            </Button>
          )}
          {onGoBack && (
            <Button
              data-testid='go-back-button'
              onClick={onGoBack}
              appearance='secondary'
              size={sizeConfig.buttonSize}
            >
              Go Back
            </Button>
          )}
          {onReportBug && (
            <Button
              data-testid='report-bug-button'
              onClick={onReportBug}
              appearance='secondary'
              size={sizeConfig.buttonSize}
            >
              Report a Bug
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

const renderCompactPermanentInline = (
  props: ErrorFallbackUIProps,
  colors: ColorScheme,
  sizeConfig: SizeConfig,
): ReactNode => {
  const { title, message, onReportBug, testId } = props;
  const containerClass = `${sizeConfig.padding} m-y-1 ${colors.bgColor} ${colors.borderColor} br-1`;

  return (
    <Flex
      data-testid={testId}
      direction='row'
      alignItems='center'
      gap={sizeConfig.gap}
      className={containerClass}
    >
      <Icon
        svg={WarningIcon}
        size={sizeConfig.iconSize}
        className={colors.iconColor}
      />
      <Text size={sizeConfig.textSize} className={colors.textColor}>
        <strong>{title}</strong>
        {message && ` ${message}`}
      </Text>
      {onReportBug && (
        <Button
          data-testid='report-bug-button'
          onClick={onReportBug}
          size={sizeConfig.buttonSize}
          appearance='secondary'
        >
          Report
        </Button>
      )}
    </Flex>
  );
};

const renderFullPermanentInline = (
  props: ErrorFallbackUIProps,
  colors: ColorScheme,
  sizeConfig: SizeConfig,
): ReactNode => {
  const { title, message, onReportBug, testId } = props;
  const containerClass = `${sizeConfig.padding} m-y-1 ${colors.bgColor} ${colors.borderColor} br-1`;

  return (
    <Flex
      data-testid={testId}
      direction='column'
      gap={sizeConfig.gap}
      className={containerClass}
    >
      <Flex direction='row' alignItems='center' gap={sizeConfig.gap}>
        <Icon
          svg={WarningIcon}
          size={sizeConfig.iconSize}
          className={colors.iconColor}
        />
        <Text className={colors.textColor}>
          <strong>{title}</strong>
        </Text>
      </Flex>
      {message && (
        <Text size={sizeConfig.textSize} className={colors.textColor}>
          {message}
        </Text>
      )}
      {onReportBug && (
        <Button
          data-testid='report-bug-button'
          onClick={onReportBug}
          size={sizeConfig.buttonSize}
          appearance='secondary'
        >
          Report a Bug
        </Button>
      )}
    </Flex>
  );
};

const renderCompactInline = (
  props: ErrorFallbackUIProps,
  colors: ColorScheme,
  sizeConfig: SizeConfig,
): ReactNode => {
  const { title, message, onRetry, onReportBug, testId } = props;
  const containerClass = `${sizeConfig.padding} m-y-1 ${colors.bgColor} ${colors.borderColor} br-1`;

  return (
    <Flex
      data-testid={testId}
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      className={containerClass}
    >
      <Flex direction='row' alignItems='center' gap={sizeConfig.gap}>
        <Icon
          svg={WarningIcon}
          size={sizeConfig.iconSize}
          className={colors.iconColor}
        />
        <Text size={sizeConfig.textSize} className={colors.textColor}>
          <strong>{title}</strong>
          {message && ` ${message}`}
        </Text>
      </Flex>
      <Flex direction='row' gap={1}>
        {onRetry && (
          <Button
            data-testid='retry-button'
            onClick={onRetry}
            size={sizeConfig.buttonSize}
            appearance='secondary'
          >
            Retry
          </Button>
        )}
        {onReportBug && (
          <Button
            data-testid='report-bug-button'
            onClick={onReportBug}
            size={sizeConfig.buttonSize}
            appearance='secondary'
          >
            Report
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

const renderFullInline = (
  props: ErrorFallbackUIProps,
  colors: ColorScheme,
  sizeConfig: SizeConfig,
): ReactNode => {
  const { title, message, onRetry, onReportBug, testId } = props;
  const containerClass = `${sizeConfig.padding} m-y-1 ${colors.bgColor} ${colors.borderColor} br-1`;

  return (
    <Flex
      data-testid={testId}
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      className={containerClass}
    >
      <Flex direction='row' alignItems='center' gap={sizeConfig.gap}>
        <Icon
          svg={WarningIcon}
          size={sizeConfig.iconSize}
          className={colors.iconColor}
        />
        <Flex direction='column' gap={0}>
          <Text className={colors.textColor}>
            <strong>{title}</strong>
          </Text>
          {message && (
            <Text size={sizeConfig.textSize} className={colors.textColor}>
              {message}
            </Text>
          )}
        </Flex>
      </Flex>
      <Flex direction='row' gap={1}>
        {onRetry && (
          <Button
            data-testid='retry-button'
            onClick={onRetry}
            size={sizeConfig.buttonSize}
            appearance='secondary'
          >
            Retry
          </Button>
        )}
        {onReportBug && (
          <Button
            data-testid='report-bug-button'
            onClick={onReportBug}
            size={sizeConfig.buttonSize}
            appearance='secondary'
          >
            Report
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

/**
 * Shared error fallback UI component used by FeatureErrorBoundary and ModuleErrorBoundary.
 *
 * Supports different layouts, sizes, and severities to accommodate various use cases
 * from full-page feature errors to small module warnings.
 */
export const ErrorFallbackUI = (props: ErrorFallbackUIProps): ReactNode => {
  const { severity, layout, size, permanent = false } = props;
  const colors = getColorScheme(severity, permanent);
  const sizeConfig = getSizeConfig(size, layout);

  if (layout === 'centered') {
    return renderCenteredLayout(props, colors, sizeConfig);
  }

  const isFull = size === 'full';
  if (permanent) {
    return isFull
      ? renderFullPermanentInline(props, colors, sizeConfig)
      : renderCompactPermanentInline(props, colors, sizeConfig);
  }

  return isFull
    ? renderFullInline(props, colors, sizeConfig)
    : renderCompactInline(props, colors, sizeConfig);
};
